import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useI18n } from '../../context/I18nContext';
import { toast } from 'sonner';
import { Receipt, ExternalLink, Download, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingHistory = () => {
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/billing/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setInvoices(await res.json());
    } catch (e) {
      toast.error('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'open': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'void': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      case 'uncollectible': return 'bg-red-500/10 text-red-400 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('billing.title')}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title={t('billing.title')}>
      <div className="space-y-6" data-testid="billing-history-page">
        <p className="text-muted-foreground">{t('billing.subtitle')}</p>

        {invoices.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Receipt className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">{t('billing.noInvoices')}</h3>
              <p className="text-muted-foreground">{t('billing.noInvoicesDesc')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv, i) => (
              <Card
                key={inv.id}
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${i * 0.04}s` }}
                data-testid={`invoice-${inv.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-gold/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-gold" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{inv.number || inv.id.slice(0, 20)}</p>
                        <p className="text-sm text-muted-foreground truncate">{inv.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6 shrink-0 flex-wrap">
                      <div className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(inv.created)}
                      </div>
                      <div className="font-semibold whitespace-nowrap">${inv.amount_paid.toFixed(2)}</div>
                      <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border whitespace-nowrap ${getStatusStyle(inv.status)}`}>
                        {inv.status}
                      </span>
                      <div className="flex items-center gap-2">
                        {inv.hosted_invoice_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-gold"
                            onClick={() => window.open(inv.hosted_invoice_url, '_blank')}
                            data-testid={`view-invoice-${inv.id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                        {inv.invoice_pdf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-gold"
                            onClick={() => window.open(inv.invoice_pdf, '_blank')}
                            data-testid={`download-invoice-${inv.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BillingHistory;
